import { useExerciseIdsSelect } from '@entities/exercise-video/model.ts';
import cn from 'classnames';
import { memo } from 'react';
import { useParams } from 'react-router-dom';
import { useExercisePageRedirect } from '@pages/programs/exercise/helpers.ts';
import { ListMenu } from '@widgets/exercises/index.ts';
import { InfinityList } from '@widgets/shared/InfinityList/index.tsx';
import { SelectingIdsIconCheckbox } from '@widgets/shared/SelectingIconCheckbox/index.ts';
import { ExerciseCardSkeleton } from '@features/exercises/exercise-card/Skeleton/ui.tsx';
import {
  ActionWrapper,
  ExerciseCard,
  ExerciseCardMobile,
  ExerciseDuration,
} from '@features/exercises/index.ts';
import { Check, LibraryPlayButton } from '@shared/assets/index.ts';
import { useMobile } from '@shared/hooks/index.ts';
import { useSaveScroll } from '@shared/hooks/use-save-scroll.ts';
import { useOneVideoSelect } from '@shared/ui/forms/video/helpers.ts';
import { IconButton } from '@shared/ui/index.ts';
import { Skeleton } from '@shared/ui/Skeleton/ui.tsx';
import { Blank } from 'library/components/common';
import { useVideoList, type VideoListParams } from './model';
import * as st from './styles.module.scss';

interface Props {
  forQuery: Omit<VideoListParams, 'library' | 'module' | 'page'>;
  showFilterBar: boolean;
  isOneVideoSelect?: boolean;
}

export const ExerciseList = memo(({ forQuery, showFilterBar, isOneVideoSelect }: Props) => {
  const { isIdSelected, doToggleId, isSelecting } = useExerciseIdsSelect();
  const { doResetSelectedIds: doResetOneVideo, isIdSelected: isOneVideoSelected } =
    useOneVideoSelect();
  const { code } = useParams<{ code: string }>();
  const { setListScrollData } = useSaveScroll('ExerciseListScrollData');
  const { doRedirectWithSavePageUrl } = useExercisePageRedirect();
  const { videos, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, isError } =
    useVideoList({
      ...forQuery,
      library: true,
    });

  const { isMobile } = useMobile();

  const goToExercise = (id: number): void => {
    setListScrollData({ scrollY: window.scrollY });
    doRedirectWithSavePageUrl(`/programs/${code}/exercise/${id}`);
  };

  const isChecked = (id: number) => {
    return isOneVideoSelect ? isOneVideoSelected(id) : isIdSelected(id);
    // const exercises = selected;
    // return !!exercises.find((selectedId) => selectedId === id);
  };

  const onExerciseSelect = (id: number) => {
    if (isOneVideoSelect) {
      doResetOneVideo([id]);
    } else {
      doToggleId(id);
    }
  };

  if (isError) {
    return <Blank text="Возникли технические неполадки, обратитесь в поддержку" />;
  }

  if (isLoading) {
    return (
      <div className={st.videos}>
        <Skeleton
          listLength={16}
          className={st.scroller}
          skeletonItem={(i) => <ExerciseCardSkeleton key={i + 'ExerciseListSkeletonItem'} />}
        />
      </div>
    );
  }

  return (
    <div className={st.videos}>
      <InfinityList
        hasMore={hasNextPage}
        loadMore={fetchNextPage}
        loading={isFetchingNextPage}
        className={cn(st.scroller, showFilterBar && st.withFilterOn)}
      >
        {videos.map((video) =>
          isMobile ? (
            <ExerciseCardMobile
              key={video.id}
              exerciseVideo={video}
              onExerciseClick={() => goToExercise(video.id)}
              RightBtn={isSelecting ? undefined : <ListMenu videoId={video.id} />}
              LeftBtn={
                isSelecting ? (
                  <SelectingIdsIconCheckbox
                    shouldBeSelectOne={isOneVideoSelect}
                    id={video.id}
                    name={'useExerciseIdsSelect'}
                    onClick={(e) => {
                      e.stopPropagation();
                      onExerciseSelect(video.id);
                    }}
                    checked={isChecked(video.id)}
                  />
                ) : undefined
              }
            />
          ) : (
            <ExerciseCard
              key={video.id}
              exerciseVideo={video}
              onClick={() => {
                isSelecting ? onExerciseSelect(video.id) : goToExercise(video.id);
              }}
            >
              <ActionWrapper
                variant={isSelecting ? 'solid' : undefined}
                alwaysVisible={isSelecting ? isChecked(video.id) : undefined}
                Duration={<ExerciseDuration exerciseVideo={video} />}
                Play={
                  <IconButton variant="white" type="button" onClick={() => goToExercise(video.id)}>
                    <LibraryPlayButton className={st.playButton} />
                  </IconButton>
                }
                Select={
                  isSelecting ? (
                    <div className={cn(st.checkbox, isChecked(video.id) && st.checked)}>
                      <Check />
                    </div>
                  ) : undefined
                }
                Menu={isSelecting ? undefined : <ListMenu videoId={video.id} />}
              />
            </ExerciseCard>
          )
        )}
        <div className={st.loader}>
          {!hasNextPage && !videos.length && (
            <Blank
              text={
                Object.keys(forQuery).length
                  ? 'По вашему запросу ничего не найдено'
                  : 'В этом списке нет видео'
              }
            />
          )}
        </div>
      </InfinityList>
    </div>
  );
});
